import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLoanInput,
  Loan,
  LoanWithDetails,
  RecordLoanPaymentInput,
  RecordLoanPaymentResult,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useLoans(companyId: string | null) {
  return useQuery({
    queryKey: ['loans', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () => api.get<{ loans: Loan[] }>(`/companies/${companyId}/loans`).then((r) => r.loans),
  });
}

export function useLoan(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['loan', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () => api.get<{ loan: LoanWithDetails }>(`/companies/${companyId}/loans/${id}`).then((r) => r.loan),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['loans', c] });
  qc.invalidateQueries({ queryKey: ['loan', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateLoan(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLoanInput) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ loan: LoanWithDetails }>(`/companies/${companyId}/loans`, input).then((r) => r.loan);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRecordLoanPayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordLoanPaymentInput }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ result: RecordLoanPaymentResult }>(`/companies/${companyId}/loans/${id}/payments`, input).then((r) => r.result);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
